import { ChangeDetectionStrategy, Component, Injector, Input } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { HotelPageInfoStore } from '../../services/hotel-page-info.store';
import { PostReviewModalComponent } from '../post-review-modal/post-review-modal.component';

import { mergeReviewSources } from '@helpers/review-list';
import { DisplayableHotelReview } from '@interfaces/hotel-review';
import { Hotel } from '@interfaces/services/api/tours-catalog.endpoint';

@Component({
  selector: 'app-hotel-reviews',
  templateUrl: './hotel-reviews.component.html',
  styleUrls: ['./hotel-reviews.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HotelReviewsComponent {
  @Input() hotel: Hotel | null = null;
  @Input() reviews: DisplayableHotelReview[] = [];
  ownReviews$: Observable<DisplayableHotelReview[]> = this.hotelPageInfo.onChanges('ownReviews');
  ownLoading$: Observable<boolean> = this.hotelPageInfo.onChanges('ownReviewsLoading');
  ownLastPage$: Observable<boolean> = this.hotelPageInfo.onChanges('ownLastReviewsPage');
  ownError$: Observable<boolean> = this.hotelPageInfo.onChanges('ownReviewsError');
  isLoading$: Observable<boolean> = this.hotelPageInfo.onChanges('reviewsLoading');
  paginationLimited$: Observable<boolean> = this.hotelPageInfo.onChanges('reviewsPaginationLimited');
  lastPage$: Observable<boolean> = this.hotelPageInfo.onChanges('isLastReviewsPage');
  error$: Observable<boolean> = this.hotelPageInfo.onChanges('reviewsError');
  pagination$ = combineLatest([
    this.lastPage$, this.error$, this.isLoading$,
    this.ownLastPage$, this.ownError$, this.ownLoading$
  ]).pipe(map(([last, error, loading, ownLast, ownError, ownLoading]) => ({
    hasNext: !last || !ownLast,
    canLoadMore: (!last && !error && !loading) || (!ownLast && !ownError && !ownLoading),
    loading: loading || ownLoading,
    empty: last && ownLast && !error && !ownError
  })));

  constructor(
    private readonly hotelPageInfo: HotelPageInfoStore,
    private readonly modalService: NgbModal,
    private readonly injector: Injector
  ) { }

  mixedReviews(ownReviews: DisplayableHotelReview[]): DisplayableHotelReview[] {
    return mergeReviewSources(this.reviews, ownReviews);
  }

  loadNextPage(): void {
    const state = this.hotelPageInfo.state;
    if (!state.isLastReviewsPage && !state.reviewsLoading && !state.reviewsError) {
      this.hotelPageInfo.loadNextPage();
    }
    if (!state.ownLastReviewsPage && !state.ownReviewsLoading && !state.ownReviewsError) {
      this.hotelPageInfo.loadNextPage('site');
    }
  }

  retryOwn(): void {
    this.hotelPageInfo.retryReviews('site');
  }

  retry(): void {
    this.hotelPageInfo.retryReviews();
  }

  trackByFn(idx: number, item: DisplayableHotelReview): string {
    return `${item.type}:${item._id}`;
  }

  openPostReviewModal(): void {
    this.modalService.open(PostReviewModalComponent, { injector: this.injector, size: 'lg', centered: true });
  }
}
